import React, { useState, useRef, useEffect, useCallback } from 'react';

// Vibrant color palette matching the yellow theme
const cardColors = [
  { bg: 'linear-gradient(135deg, #FFE566 0%, #FFD93D 100%)', text: '#1a1a1a', accent: '#FF6B6B' }, // Yellow
  { bg: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)', text: '#ffffff', accent: '#FFE566' }, // Coral
  { bg: 'linear-gradient(135deg, #4ECDC4 0%, #45B7AA 100%)', text: '#ffffff', accent: '#FFE566' }, // Teal
  { bg: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)', text: '#ffffff', accent: '#FFE566' }, // Purple
  { bg: 'linear-gradient(135deg, #F472B6 0%, #EC4899 100%)', text: '#ffffff', accent: '#FFE566' }, // Pink
  { bg: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)', text: '#ffffff', accent: '#FFE566' }, // Blue
  { bg: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', text: '#ffffff', accent: '#FFE566' }, // Green
  { bg: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)', text: '#1a1a1a', accent: '#FF6B6B' }, // Amber
  { bg: 'linear-gradient(135deg, #FB923C 0%, #F97316 100%)', text: '#ffffff', accent: '#FFE566' }, // Orange
  { bg: 'linear-gradient(135deg, #E879F9 0%, #D946EF 100%)', text: '#ffffff', accent: '#FFE566' }, // Fuchsia
];

// Quote data with person info
const quotesData = [
  {
    id: 1,
    quote: "Fight for the things that you care about, but do it in a way that will lead others to join you.",
    name: "Ruth Bader Ginsburg",
    title: "Supreme Court Justice",
  },
  {
    id: 2,
    quote: "Greatness comes from pain and suffering. If you want to do something extraordinary, it will be hard.",
    name: "Jensen Huang",
    title: "NVIDIA CEO",
  },
  {
    id: 3,
    quote: "You can't connect the dots looking forward; you can only connect them looking backwards.",
    name: "Steve Jobs",
    title: "Apple Co-founder",
  },
  {
    id: 4,
    quote: "I wanted to build a company that made women feel confident and safe making the first move.",
    name: "Whitney Wolfe Herd",
    title: "Bumble Founder",
  },
  {
    id: 5,
    quote: "The biggest adventure you can take is to live the life of your dreams.",
    name: "Oprah Winfrey",
    title: "Media Executive",
  },
  {
    id: 6,
    quote: "Luck has nothing to do with it.",
    name: "Serena Williams",
    title: "Tennis Champion",
  },
  {
    id: 7,
    quote: "Change will not come if we wait for some other person or some other time.",
    name: "Barack Obama",
    title: "44th U.S. President",
  },
  {
    id: 8,
    quote: "If you double the number of experiments you do per year, you're going to double your inventiveness.",
    name: "Jeff Bezos",
    title: "Amazon Founder",
  },
  {
    id: 9,
    quote: "If you're not embarrassed by the first version of your product, you launched too late.",
    name: "Reid Hoffman",
    title: "LinkedIn Co-founder",
  },
  {
    id: 10,
    quote: "The best ideas look bad at first.",
    name: "Sam Altman",
    title: "OpenAI CEO",
  },
  {
    id: 11,
    quote: "Done is better than perfect.",
    name: "Sheryl Sandberg",
    title: "Former Meta COO",
  },
  {
    id: 12,
    quote: "Success isn't about how much money you make. It's about the difference you make.",
    name: "Michelle Obama",
    title: "Former First Lady",
  },
  {
    id: 13,
    quote: "Not everything that is faced can be changed, but nothing can be changed until it is faced.",
    name: "James Baldwin",
    title: "Author & Activist",
  },
  {
    id: 14,
    quote: "I've failed over and over and over again in my life. And that is why I succeed.",
    name: "Michael Jordan",
    title: "Basketball Legend",
  },
  {
    id: 15,
    quote: "I'd rather regret the risks that didn't work out than the chances I didn't take.",
    name: "Simone Biles",
    title: "Olympic Gymnast",
  },
];

// Individual Quote Card Component
const QuoteCard = ({ quote, isCenter, flipProgress, index }) => {
  const rotateY = flipProgress * 12; // Subtle rotation based on position
  const colorScheme = cardColors[index % cardColors.length];
  const isLightBg = colorScheme.text === '#1a1a1a';

  return (
    <div
      className="quote-card flex-shrink-0 relative w-[300px] h-[380px] md:w-[340px] md:h-[440px] rounded-3xl overflow-hidden"
      style={{
        background: colorScheme.bg,
        transform: `perspective(1000px) rotateY(${rotateY}deg) scale(${isCenter ? 1.08 : 0.92})`,
        opacity: isCenter ? 1 : 0.75,
        transition: 'transform 0.3s ease-out, opacity 0.3s ease-out, box-shadow 0.3s ease-out',
        willChange: 'transform, opacity',
        boxShadow: isCenter
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
          : '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Decorative Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 80%, ${isLightBg ? '#000' : '#fff'} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      />

      {/* Decorative shapes */}
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20"
        style={{ background: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)' }}
      />
      <div
        className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-15"
        style={{ background: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)' }}
      />

      {/* Quote Content */}
      <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
        {/* Quote Icon */}
        <div
          className="text-7xl font-serif leading-none opacity-25"
          style={{ color: colorScheme.text }}
        >
          "
        </div>

        {/* Quote Text */}
        <blockquote
          className="text-lg md:text-xl font-semibold leading-relaxed flex-grow flex items-center"
          style={{ color: colorScheme.text, fontFamily: "'Inter', sans-serif" }}
        >
          {quote.quote}
        </blockquote>

        {/* Author Info */}
        <div className="flex items-center gap-3 mt-4">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shadow-lg"
            style={{
              background: isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.25)',
              color: colorScheme.text,
            }}
          >
            {quote.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p
              className="font-bold text-sm"
              style={{ color: colorScheme.text, fontFamily: "'Montserrat', sans-serif" }}
            >
              {quote.name}
            </p>
            <p
              className="text-xs font-medium"
              style={{ color: isLightBg ? 'rgba(26,26,26,0.6)' : 'rgba(255,255,255,0.7)' }}
            >
              {quote.title}
            </p>
          </div>
        </div>
      </div>

      {/* Shine effect for center card */}
      {isCenter && (
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, transparent 100%)',
          }}
        />
      )}
    </div>
  );
};

// Main Carousel Component
const QuoteCarousel = ({ quotes = quotesData }) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);
  const [centerIndex, setCenterIndex] = useState(Math.floor(quotes.length / 2));
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const rafRef = useRef(null);
  const targetTranslateRef = useRef(0);

  const cardWidth = 360; // Card width (340px) + gap (20px)
  const maxTranslate = (quotes.length - 1) * cardWidth;

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Smooth animation loop
  const animate = useCallback(() => {
    setTranslateX(prev => {
      const diff = targetTranslateRef.current - prev;
      if (Math.abs(diff) < 0.5) return targetTranslateRef.current;
      return prev + diff * 0.1; // Easing factor
    });
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  // Mouse movement handler (desktop)
  const handleMouseMove = useCallback((e) => {
    if (isMobile || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const percentage = mouseX / rect.width;

    // Map mouse position to translate value
    const newTranslate = percentage * maxTranslate;
    targetTranslateRef.current = Math.max(0, Math.min(newTranslate, maxTranslate));

    // Calculate center card index
    const newCenterIndex = Math.round(targetTranslateRef.current / cardWidth);
    setCenterIndex(Math.max(0, Math.min(newCenterIndex, quotes.length - 1)));
  }, [isMobile, maxTranslate, cardWidth, quotes.length]);

  // Touch handlers (mobile)
  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setScrollLeft(translateX);
  }, [translateX]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const x = e.touches[0].clientX;
    const walk = (startX - x) * 1.5;
    const newTranslate = Math.max(0, Math.min(scrollLeft + walk, maxTranslate));
    targetTranslateRef.current = newTranslate;

    const newCenterIndex = Math.round(newTranslate / cardWidth);
    setCenterIndex(Math.max(0, Math.min(newCenterIndex, quotes.length - 1)));
  }, [isDragging, startX, scrollLeft, maxTranslate, cardWidth, quotes.length]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // Snap to nearest card
    const snappedTranslate = Math.round(targetTranslateRef.current / cardWidth) * cardWidth;
    targetTranslateRef.current = Math.max(0, Math.min(snappedTranslate, maxTranslate));
  }, [cardWidth, maxTranslate]);

  // Mouse drag handlers (desktop fallback)
  const handleMouseDown = useCallback((e) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(translateX);
  }, [isMobile, translateX]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      const snappedTranslate = Math.round(targetTranslateRef.current / cardWidth) * cardWidth;
      targetTranslateRef.current = Math.max(0, Math.min(snappedTranslate, maxTranslate));
    }
  }, [isDragging, cardWidth, maxTranslate]);

  const handleMouseDrag = useCallback((e) => {
    if (!isDragging || !isMobile) return;
    const x = e.clientX;
    const walk = (startX - x) * 1.5;
    const newTranslate = Math.max(0, Math.min(scrollLeft + walk, maxTranslate));
    targetTranslateRef.current = newTranslate;

    const newCenterIndex = Math.round(newTranslate / cardWidth);
    setCenterIndex(Math.max(0, Math.min(newCenterIndex, quotes.length - 1)));
  }, [isDragging, isMobile, startX, scrollLeft, maxTranslate, cardWidth, quotes.length]);

  return (
    <div className="w-full py-12 md:py-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFF9F0 0%, #FFFDF5 100%)' }}>
      {/* Section Header */}
      <div className="text-center mb-10 px-6">
        <p className="text-[#FF6B6B] text-sm font-bold uppercase tracking-widest mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Words of Wisdom
        </p>
        <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          From Those Who Dared
        </h2>
        {!isMobile && (
          <p className="text-[#1a1a1a]/40 text-sm font-medium">
            Move your mouse left and right to explore
          </p>
        )}
      </div>

      {/* Carousel Container */}
      <div
        ref={containerRef}
        className="relative w-full cursor-grab active:cursor-grabbing"
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMoveCapture={handleMouseDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-5 px-6 md:px-12 py-4"
          style={{
            transform: `translateX(-${translateX}px)`,
            willChange: 'transform',
          }}
        >
          {quotes.map((quote, index) => {
            const cardPosition = index * cardWidth - translateX;
            const containerWidth = containerRef.current?.offsetWidth || 800;
            const centerPosition = containerWidth / 2 - cardWidth / 2;
            const distanceFromCenter = (cardPosition - centerPosition) / cardWidth;

            return (
              <QuoteCard
                key={quote.id}
                quote={quote}
                index={index}
                isCenter={index === centerIndex}
                flipProgress={Math.max(-1, Math.min(1, distanceFromCenter))}
              />
            );
          })}
        </div>

        {/* Navigation Dots - compact for many quotes */}
        <div className="flex justify-center items-center gap-1.5 mt-8 px-4">
          {quotes.map((_, index) => {
            const isActive = index === centerIndex;
            const isNear = Math.abs(index - centerIndex) <= 2;
            const colorScheme = cardColors[index % cardColors.length];

            return (
              <button
                key={index}
                className={`rounded-full transition-all duration-300 ${
                  isActive
                    ? 'w-8 h-3'
                    : isNear
                    ? 'w-2.5 h-2.5 hover:scale-125'
                    : 'w-1.5 h-1.5 opacity-40 hover:opacity-70'
                }`}
                style={{
                  background: isActive ? colorScheme.bg : '#1a1a1a',
                }}
                onClick={() => {
                  targetTranslateRef.current = index * cardWidth;
                  setCenterIndex(index);
                }}
                aria-label={`Go to quote ${index + 1} by ${quotes[index].name}`}
              />
            );
          })}
        </div>

        {/* Quote Counter */}
        <p className="text-center text-[#1a1a1a]/50 text-sm mt-4 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {centerIndex + 1} / {quotes.length}
        </p>
      </div>

      {/* Mobile Swipe Hint */}
      {isMobile && (
        <p className="text-center text-[#1a1a1a]/40 text-xs mt-2 font-medium">
          Swipe to explore more quotes
        </p>
      )}

      <style>{`
        .quote-card {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform-style: preserve-3d;
        }
      `}</style>
    </div>
  );
};

export default QuoteCarousel;
